import {Injectable} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {Query} from '../types-repository';
import {GET_FOLDER_SECURITY_LEVEL} from '../queries-repository';

/** The security level that obliges an encode to carry a forensic watermark (VTK-3544). */
export const WATERMARK_REQUIRED_LEVEL = 'prerelease_security';

/**
 * The security level of a folder's bucket (its `security_level` tag), looked up once per bucket.
 *
 * The lookup goes through the folder itself rather than the bucket root: a user may browse a folder without
 * holding a grant on its bucket's root.
 */
@Injectable({
  providedIn: 'root'
})
export class SecurityLevelService {
  private readonly byBucket = new Map<string, Observable<string | null>>();

  constructor(private apollo: Apollo) {
  }

  /** `folderId` is an `s3://bucket/path/` URL. Emits null if the level is unknown. */
  securityLevel(folderId: string): Observable<string | null> {
    const bucket = folderId.replace(/^s3:\/\//, '').split('/')[0];
    if (!this.byBucket.has(bucket)) {
      this.byBucket.set(bucket, this.apollo.query<Query>({
        query: GET_FOLDER_SECURITY_LEVEL,
        variables: {id: folderId},
      }).pipe(
        map(r => r.data?.folder?.security_level ?? null),
        catchError(err => {
          // Not knowing is not "no", but the workflow enforces the rule regardless; only the form's hint is lost.
          console.error(`security_level(${folderId}) failed`, err);
          this.byBucket.delete(bucket);
          return of(null);
        }),
        shareReplay(1),
      ));
    }
    return this.byBucket.get(bucket);
  }
}
